import get from 'lodash.get';
import has from 'lodash.has';
import moment from 'moment';

// Global Types
import { AppState } from 'services/types';

// Global Utils
import createDeepEqualSelector from 'utils/reselect/typescript/createDeepEqualSelector';

// Local Types
import {
    HarvestEventDocumentsState,
    HarvestEventDocumentsProps
} from './types';

const selectHarvestEventState = (
    state: Record<string, any>
): HarvestEventDocumentsState => state.harvestEventDocuments;

const selectDocumentUuid = (
    state: AppState,
    props: HarvestEventDocumentsProps
): string | undefined => props.documentUuid;

const selectHarvestEventDocuments = createDeepEqualSelector(
    [selectHarvestEventState],
    harvestEventDocumentsStateState => {
        const { data } = harvestEventDocumentsStateState;
        return data;
    }
);

const selectHarvestEventDocumentsArray = createDeepEqualSelector(
    selectHarvestEventDocuments,
    harvestEventDocumentsState => Object.values(harvestEventDocumentsState)
);

const selectHarvestEventTimesheetDocumentsArray = createDeepEqualSelector(
    selectHarvestEventDocumentsArray,
    harvestEventDocumentsArray =>
        harvestEventDocumentsArray.filter(document =>
            has(document, 'harvestTimesheet.externalId')
        )
);

const selectBinTotalsByPersonAndDate = createDeepEqualSelector(
    [selectHarvestEventTimesheetDocumentsArray],
    harvestEventTimesheetDocumentsArray => {
        return harvestEventTimesheetDocumentsArray.reduce<Record<string, any>>(
            (accumulator, currentValue) => {
                const { personIds, submittedAt } = currentValue;

                if (!personIds || !personIds.length) {
                    return accumulator;
                }

                const date = moment(submittedAt).format('YYYY-MM-DD');
                const binsPerPerson = 1 / personIds.length;

                personIds.forEach(personId => {
                    const binTotalsByDate = accumulator[date] || {};
                    const personBinTotal = binTotalsByDate[personId] || 0;
                    binTotalsByDate[personId] = personBinTotal + binsPerPerson;
                    accumulator[date] = binTotalsByDate;
                });

                return accumulator;
            },
            {}
        );
    }
);

const selectHarvestEventDocument = createDeepEqualSelector(
    [selectHarvestEventDocuments, selectDocumentUuid],
    (harvestEventDocumentsState, documentUuid) => {
        if (!documentUuid) {
            return undefined;
        }
        return get(harvestEventDocumentsState, documentUuid);
    }
);

const makeSelectHarvestEventDocument = (): typeof selectHarvestEventDocument =>
    selectHarvestEventDocument;

const selectHarvestEventDocumentCreatedAt = createDeepEqualSelector(
    selectHarvestEventDocument,
    harvestEventState => get(harvestEventState, 'createdAt')
);

const makeSelectHarvestEventDocumentCreatedAt = (): typeof selectHarvestEventDocumentCreatedAt =>
    selectHarvestEventDocumentCreatedAt;

export {
    selectHarvestEventDocuments,
    selectHarvestEventDocumentsArray,
    selectHarvestEventTimesheetDocumentsArray,
    selectBinTotalsByPersonAndDate,
    makeSelectHarvestEventDocument,
    makeSelectHarvestEventDocumentCreatedAt
};
